import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onSnapshot, query, where } from "firebase/firestore";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const formatDate = (value) => {
  if (!value) return "";
  const date = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

const currency = (amount) =>
  `$${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const MaintenancePartsRequests = ({ userId }) => {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedProperty || !userId) return undefined;
    setLoading(true);
    const partsRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "partsRequests"
    );
    // Only the signed-in user's cart-based requests (legacy single-item
    // requests don't carry an `items` array and are excluded below).
    const q = query(partsRef, where("requestedBy", "==", userId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rows = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((r) => Array.isArray(r.items))
        .sort((a, b) => {
          const at = a.requestedAt?.seconds || 0;
          const bt = b.requestedAt?.seconds || 0;
          return bt - at;
        });
      setRequests(rows);
      setLoading(false);
    });
    return unsubscribe;
  }, [selectedProperty, userId]);

  const content = useMemo(() => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      );
    }
    if (requests.length === 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          py={8}
          color="text.secondary"
        >
          <Inventory2Icon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
          <Typography>No parts requests yet</Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={1.5}>
        {requests.map((req) => {
          const itemCount =
            req.itemCount ??
            req.items.reduce((sum, i) => sum + Number(i.count || 0), 0);
          return (
            <Card key={req.id} variant="outlined" sx={{ p: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {formatDate(req.requestedAt)}
                </Typography>
                <Chip
                  size="small"
                  label={req.status || "pending"}
                  color={STATUS_COLOR[req.status] || "default"}
                  sx={{ textTransform: "capitalize" }}
                />
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {itemCount} item{itemCount !== 1 ? "s" : ""} ·{" "}
                  {req.items.length} line{req.items.length !== 1 ? "s" : ""}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {currency(req.totalEstimated)}
                </Typography>
              </Box>
            </Card>
          );
        })}
      </Stack>
    );
  }, [loading, requests]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 720, mx: "auto" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={600}>
          Parts Requests
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddShoppingCartIcon />}
          onClick={() => navigate("/parts/new")}
        >
          Request Parts
        </Button>
      </Box>
      {content}
    </Box>
  );
};

export default MaintenancePartsRequests;
